import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function SiteFooter() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitState, setSubmitState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);
    const hpValue = (formData.get('website_alt') as string || '').trim();
    const emailValue = (formData.get('email') as string || '').trim();

    if (hpValue) {
      setSubmitState('success');
      setEmail('');
      return;
    }

    if (!emailValue) {
      setFormError('Пожалуйста, введите email');
      return;
    }

    setSubmitState('loading');
    setFormError('');

    formData.delete('website_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d958pg5568obap8dbg30', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setSubmitState('success');
        setEmail('');
        setTimeout(() => setSubmitState('idle'), 4000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setSubmitState('success');
          setEmail('');
          setTimeout(() => setSubmitState('idle'), 4000);
        } else {
          setFormError(serverMsg || 'Произошла ошибка при подписке');
          setSubmitState('error');
        }
      }
    } catch {
      setFormError('Не удалось подключиться к серверу');
      setSubmitState('error');
    }
  };

  return (
    <footer className="w-full bg-foreground-950 py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
          <div>
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
              {t('footer.subscribe')}
            </h3>
            <p className="text-sm text-foreground-300 mb-6 max-w-xs">
              {t('footer.subscribe_text')}
            </p>
            <form onSubmit={handleSubscribe} data-readdy-form="">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-hp"
              />
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footer.email_placeholder')}
                  className="flex-1 bg-transparent border-b border-foreground-600 text-background-50 text-sm px-2 py-2.5 focus:outline-none focus:border-accent-400 transition-colors"
                  required
                />
                <button
                  type="submit"
                  disabled={submitState === 'loading'}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-background-50 text-foreground-950 text-sm font-semibold hover:bg-background-200 transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {submitState === 'loading' ? '...' : t('footer.subscribe_btn')}
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
              {formError && (
                <p className="mt-2 text-xs text-red-400">{formError}</p>
              )}
              {submitState === 'success' && (
                <p className="mt-2 text-xs text-accent-400">Спасибо за подписку!</p>
              )}
            </form>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold text-background-50 mb-4">
              {t('footer.quick_links')}
            </h4>
            <nav className="flex flex-col gap-2.5">
              <Link to="/" className="text-sm text-foreground-300 hover:text-background-50 transition-colors underline underline-offset-4">
                {t('nav.home')}
              </Link>
              <Link to="/yoybuy-sheet-guide" className="text-sm text-foreground-300 hover:text-background-50 transition-colors underline underline-offset-4">
                {t('nav.guide')}
              </Link>
              <a href="#categories" className="text-sm text-foreground-300 hover:text-background-50 transition-colors underline underline-offset-4">
                {t('nav.categories')}
              </a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-300 hover:text-background-50 transition-colors underline underline-offset-4">
                Kaspreadsheet.com
              </a>
            </nav>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold text-background-50 mb-4">
              {t('footer.contact_info')}
            </h4>
            <div className="space-y-3 text-sm text-foreground-300">
              <p>{t('footer.phone')}: +44 20 1234 5678</p>
              <p>{t('footer.email')}: hello@yoybuy.autos</p>
              <h4 className="font-heading text-sm font-semibold text-background-50 pt-4">
                {t('footer.address_label')}
              </h4>
              <p>{t('footer.address')}</p>
            </div>
          </div>
        </div>

        <div className="mt-10 md:mt-14 pt-6 border-t border-foreground-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <a href="#" className="text-sm text-foreground-300 hover:text-background-50 transition-colors underline underline-offset-4">
              <i className="ri-facebook-fill"></i>
            </a>
            <a href="#" className="text-sm text-foreground-300 hover:text-background-50 transition-colors underline underline-offset-4">
              <i className="ri-instagram-line"></i>
            </a>
            <a href="#" className="text-sm text-foreground-300 hover:text-background-50 transition-colors underline underline-offset-4">
              <i className="ri-twitter-x-line"></i>
            </a>
          </div>
          <p className="text-xs text-foreground-400 text-center">
            {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="text-xs text-foreground-300 hover:text-background-50 transition-colors underline underline-offset-4 whitespace-nowrap">
              {t('footer.privacy')}
            </a>
            <a href="#" className="text-xs text-foreground-300 hover:text-background-50 transition-colors underline underline-offset-4 whitespace-nowrap">
              {t('footer.terms')}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}